import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Paperclip,
  Sparkles,
  Tag,
  PackagePlus,
  Activity,
  ShoppingBag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn, formatPrice } from "@/lib/utils";
import type { ChatMessage, Product } from "@/types";

const quickActions = [
  { label: "Nova Promo", icon: Tag },
  { label: "Importar Produtos", icon: PackagePlus },
  { label: "Status", icon: Activity },
];

const mockProducts: Product[] = [
  {
    id: "p1",
    tenant_id: "t1",
    name: "Fone Bluetooth Pro",
    description: "Fone sem fio com cancelamento de ruido",
    price: 19900,
    discount_price: 12900,
    image_url: null,
    sku: "FBP-001",
    stock: 45,
    active: true,
  },
  {
    id: "p2",
    tenant_id: "t1",
    name: "Carregador Turbo 65W",
    description: "Carregador rapido USB-C",
    price: 8900,
    discount_price: 5900,
    image_url: null,
    sku: "CT65-002",
    stock: 120,
    active: true,
  },
];

const initialMessages: ChatMessage[] = [
  {
    id: "welcome",
    role: "assistant",
    content:
      "Ola! Sou seu Copilot de disparos. Posso ajudar a criar campanhas, importar produtos, verificar status dos canais e muito mais. O que deseja fazer?",
    created_at: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
  },
];

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="h-2 w-2 rounded-full bg-muted-foreground/40"
          animate={{ y: [0, -6, 0] }}
          transition={{
            duration: 0.6,
            delay: i * 0.15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

function ProductCard({ product }: { product: Product }) {
  const hasDiscount = product.discount_price !== null;
  const discountPercent = hasDiscount
    ? Math.round((1 - product.discount_price! / product.price) * 100)
    : 0;

  return (
    <div className="flex gap-3 rounded-lg border border-border bg-secondary/30 p-3">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-secondary">
        <ShoppingBag className="h-6 w-6 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate">
          {product.name}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground truncate">
          {product.description}
        </p>
        <div className="mt-1.5 flex items-center gap-2">
          {hasDiscount ? (
            <>
              <span className="text-sm font-bold text-success">
                {formatPrice(product.discount_price!)}
              </span>
              <span className="text-xs text-muted-foreground line-through">
                {formatPrice(product.price)}
              </span>
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                -{discountPercent}%
              </Badge>
            </>
          ) : (
            <span className="text-sm font-bold text-foreground">
              {formatPrice(product.price)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.2 }}
      className={cn("flex", isUser ? "justify-end" : "justify-start")}
    >
      <div
        className={cn(
          "max-w-[85%] sm:max-w-[70%] space-y-2",
          isUser ? "items-end" : "items-start"
        )}
      >
        <div
          className={cn(
            "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
            isUser
              ? "bg-primary text-primary-foreground rounded-br-md"
              : "bg-card border border-border text-foreground rounded-bl-md"
          )}
        >
          {message.content}
        </div>

        {/* Product cards */}
        {message.products && message.products.length > 0 && (
          <div className="space-y-2 mt-2">
            {message.products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Action buttons */}
        {message.actions && message.actions.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {message.actions.map((action) => (
              <Button
                key={action.id}
                variant="outline"
                size="sm"
                className="text-xs h-7"
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}

        <p
          className={cn(
            "text-[10px] text-muted-foreground/50 px-1",
            isUser ? "text-right" : "text-left"
          )}
        >
          {new Date(message.created_at).toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </div>
    </motion.div>
  );
}

export function CopilotPage() {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  const simulateResponse = useCallback(
    (userMessage: string) => {
      setIsTyping(true);

      const timeout = setTimeout(() => {
        const isPromo = userMessage.toLowerCase().includes("promo");
        const isStatus = userMessage.toLowerCase().includes("status");

        const response: ChatMessage = {
          id: `msg-${Date.now()}`,
          role: "assistant",
          content: isPromo
            ? "Encontrei estes produtos para sua campanha. Selecione os que deseja incluir no disparo:"
            : isStatus
              ? "Voce tem 48 grupos ativos, 127 dispatches hoje com taxa de sucesso de 97.3%. Todos os canais WhatsApp estao conectados. O canal Telegram 'Flash Deals' esta desconectado."
              : "Entendido! Posso ajudar com isso. Deseja que eu crie um dispatch automatizado ou prefere configurar manualmente?",
          created_at: new Date().toISOString(),
          products: isPromo ? mockProducts : undefined,
          actions: isPromo
            ? [
                { id: "a1", label: "Selecionar Todos", type: "dispatch" },
                { id: "a2", label: "Criar Dispatch", type: "dispatch" },
              ]
            : !isStatus
              ? [
                  { id: "a1", label: "Automatizado", type: "dispatch" },
                  { id: "a2", label: "Manual", type: "config" },
                ]
              : undefined,
        };

        setMessages((prev) => [...prev, response]);
        setIsTyping(false);
      }, 1200 + Math.random() * 800);

      return () => clearTimeout(timeout);
    },
    []
  );

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) return;

    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: "user",
      content: trimmed,
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    simulateResponse(trimmed);
  }, [input, simulateResponse]);

  const handleQuickAction = useCallback(
    (label: string) => {
      setInput(label);
      const userMsg: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: "user",
        content: label,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      simulateResponse(label);
    },
    [simulateResponse]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col lg:h-[calc(100vh-7rem)]">
      {/* Hero header (shown when few messages) */}
      {messages.length <= 1 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 text-center"
        >
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-indigo-500/20">
            <Sparkles className="h-7 w-7 text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Copilot AI</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Seu assistente inteligente para disparos e gestao de canais
          </p>
        </motion.div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-4 px-2 py-4"
        role="log"
        aria-label="Mensagens do chat"
      >
        <AnimatePresence initial={false}>
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}
        </AnimatePresence>
        {isTyping && (
          <div className="flex justify-start">
            <Card className="inline-block">
              <TypingIndicator />
            </Card>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-2 px-2 py-2 overflow-x-auto">
        {quickActions.map((action) => (
          <Button
            key={action.label}
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5 text-xs"
            onClick={() => handleQuickAction(action.label)}
          >
            <action.icon className="h-3.5 w-3.5" />
            {action.label}
          </Button>
        ))}
      </div>

      {/* Input bar */}
      <div className="border-t border-border bg-card/50 backdrop-blur-sm p-3 rounded-xl">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Anexar arquivo"
          >
            <Paperclip className="h-5 w-5" />
          </Button>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Digite sua mensagem..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            aria-label="Mensagem"
          />
          <Button
            onClick={handleSend}
            size="icon"
            disabled={!input.trim()}
            className="shrink-0"
            aria-label="Enviar mensagem"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
